import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, EntityLink } from '@resource-kit/ui/react';
import { useConfirm } from '@components/ui/ConfirmDialog';
import { useToast } from '@components/ui/Toast';
import { usePermissions } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import { http, ApiError } from '@lib/api';
import { queryKeys } from '@lib/queryKeys';
import { shownSoldOut } from '../pending/soldOut';
import type { components } from '@/types/api.generated';

type DispatchPreview = components['schemas']['DispatchPreview'];
type DispatchAnomaly = DispatchPreview['anomalies'][number];
type SalesBridge = components['schemas']['SalesBridge'];

// Closed-set anomaly reason → i18n key. `satisfies` pins every backend reason
// so adding a reason without a label fails compilation here (EQD §3), not at
// runtime with a raw enum leaking into the UI.
const ANOMALY_REASON_LABEL = {
  order_not_found: 'dispatch.anomalyReason.order_not_found',
  order_cancelled: 'dispatch.anomalyReason.order_cancelled',
} as const satisfies Record<DispatchAnomaly['reason'], string>;

const BLOCK_BADGE = 'border-transparent bg-destructive/20 text-destructive';
const ATTN_BADGE = 'border-transparent bg-warning/20 text-warning';
const ROW =
  'grid grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(150px,auto)_minmax(0,1fr)_auto] items-center gap-3.5 border-t px-4 py-2.5';
// Narrow screens: the reason moves to its own second line (design `.row .why`).
const WHY = 'col-span-full order-3 text-[13px] text-muted-foreground lg:order-none lg:col-auto';

export interface PreDispatchChecksProps {
  date: string;
  /** `undefined` while the preview has not answered. */
  anomalies: DispatchAnomaly[] | undefined;
  previewError: boolean;
  /** `undefined` while the count check has not answered. */
  salesBridge: SalesBridge | undefined;
  salesBridgeError: boolean;
}

interface CheckItem {
  key: string;
  testId: string;
  /** `undefined` while the read has not answered. */
  count: number | undefined;
  error: boolean;
  tone: string;
  label: string;
  source: string;
  rows: ReactNode;
}

function Group({
  testId,
  badge,
  source,
  error,
  children,
}: {
  testId: string;
  badge: ReactNode;
  source: string;
  error: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2.5 bg-muted px-4 py-[11px]">
        {badge}
        <span className="text-[11.5px] text-muted-foreground">{source}</span>
      </div>
      {error}
      {children}
    </div>
  );
}

/**
 * The pre-dispatch checks in the awaiting-dispatch pane (#2474): what to look at before
 * dispatching. A group with nothing to show is one neutral chip on the title
 * row; only a non-zero or failed group opens into the box below (#2517). Preview anomalies would fail the run and can be cancelled in
 * place; Shopify mismatches and same-product multiples are reminders.
 */
export function PreDispatchChecks({
  date,
  anomalies,
  previewError,
  salesBridge,
  salesBridgeError,
}: PreDispatchChecksProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();
  const canWrite = usePermissions().includes(PERMISSIONS.DISPATCH_WRITE);

  // Planned → cancelled only writes D1 (`cancelPlannedJob`); a stale preview
  // gets a 409 whose backend reason is shown as-is, never retried.
  const cancelJob = useMutation<unknown, unknown, string>({
    mutationFn: (jobId) =>
      http.post(`/api/fulfillment-jobs/${encodeURIComponent(jobId)}/cancel`, undefined),
    onSuccess: () => {
      toast.success(t('job.actions.success'));
      queryClient.invalidateQueries({ queryKey: queryKeys.dispatch.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.dispatchJobs.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all() });
    },
    onError: (err) => {
      const detail = err instanceof ApiError ? err.message : t('job.actions.error.generic');
      toast.error(t('job.actions.error.title'), detail);
    },
  });

  const handleCancel = async (jobId: string) => {
    const yes = await confirm({
      title: t('job.actions.confirm.cancel.title'),
      message: t('job.actions.confirm.cancel.message'),
      type: 'danger',
      confirmTestId: 'dispatch-precheck-cancel-confirm',
    });
    if (yes) cancelJob.mutate(jobId);
  };

  const loadError = (
    <p role="alert" className="border-t px-4 py-2.5 text-[13px] text-destructive">
      {t('dispatch.preCheck.loadFailed')}
    </p>
  );
  const DASH = '—';
  // A failed request shows the error line and a dash — never a 0 that reads
  // as "all clear".
  const anomalyCount = anomalies?.length;
  const shopifyCount = salesBridge
    ? salesBridge.unpaid.length + salesBridge.missing_jobs.length
    : undefined;
  const multiQtyCount = salesBridge
    ? new Set(salesBridge.multi_qty_detail.map((row) => row.order_name)).size
    : undefined;
  const soldOut = shownSoldOut(salesBridge);
  const badgeCount = (n: number | undefined) => n ?? DASH;

  const openOrder = (to: string) => (
    <EntityLink
      to={to}
      className="rounded-md px-2.5 py-1 text-xs text-foreground no-underline hover:bg-muted hover:no-underline"
    >
      {t('dispatch.preCheck.openOrder')}
    </EntityLink>
  );

  const items: CheckItem[] = [
    {
      key: 'anomalies',
      testId: 'dispatch-precheck-group-anomalies',
      count: anomalyCount,
      error: previewError,
      tone: BLOCK_BADGE,
      label: t('dispatch.preCheck.anomalies', { count: badgeCount(anomalyCount) }),
      source: t('dispatch.preCheck.anomaliesSource'),
      rows: (
        <>
          {(anomalies ?? []).map((a) => (
            <div key={a.jobId} data-testid={`dispatch-precheck-anomaly-${a.jobId}`} className={ROW}>
              <span className="font-mono text-sm">{a.jobName ?? a.orderId}</span>
              <span className={WHY}>{t(ANOMALY_REASON_LABEL[a.reason])}</span>
              {canWrite ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2.5 text-xs"
                  data-testid={`dispatch-precheck-cancel-${a.jobId}`}
                  disabled={cancelJob.isPending}
                  onClick={() => void handleCancel(a.jobId)}
                >
                  {t('dispatch.preCheck.cancelJob')}
                </Button>
              ) : (
                <span />
              )}
            </div>
          ))}
        </>
      ),
    },
    {
      key: 'shopify',
      testId: 'dispatch-precheck-group-shopify',
      count: shopifyCount,
      error: salesBridgeError,
      tone: ATTN_BADGE,
      label: t('dispatch.preCheck.shopify', { count: badgeCount(shopifyCount) }),
      source: t('dispatch.preCheck.shopifySource'),
      rows: (
        <>
          {salesBridge?.unpaid.map((u) => (
            <div
              key={`unpaid-${u.order_name}`}
              data-testid={`dispatch-precheck-shopify-${u.order_name}`}
              className={ROW}
            >
              <span className="font-mono text-sm">{u.order_name}</span>
              <span className={WHY}>
                {t('dispatch.preCheck.unpaidWhy', { status: u.financial_status })}
              </span>
              {u.shopify_id ? openOrder(`/orders/${encodeURIComponent(u.shopify_id)}`) : <span />}
            </div>
          ))}
          {salesBridge?.missing_jobs.map((m) => (
            <div
              key={`missing-${m.order_id}`}
              data-testid={`dispatch-precheck-shopify-${m.order_name}`}
              className={ROW}
            >
              <span className="font-mono text-sm">{m.order_name}</span>
              <span className={WHY}>{t('dispatch.preCheck.missingJobWhy')}</span>
              {openOrder(`/orders/${encodeURIComponent(m.order_id)}`)}
            </div>
          ))}
        </>
      ),
    },
    {
      key: 'multiqty',
      testId: 'dispatch-precheck-group-multiqty',
      count: multiQtyCount,
      error: salesBridgeError,
      tone: ATTN_BADGE,
      label: t('dispatch.preCheck.multiQty', { count: badgeCount(multiQtyCount) }),
      source: t('dispatch.preCheck.multiQtySource'),
      rows: null,
    },
  ];
  // No sold-out item when the Shopify menu is unavailable.
  if (!(soldOut === null && salesBridge !== undefined)) {
    items.push({
      key: 'soldout',
      testId: 'dispatch-precheck-group-soldout',
      count: soldOut?.length,
      error: salesBridgeError,
      tone: ATTN_BADGE,
      label: t('dispatch.preCheck.soldOut', { count: badgeCount(soldOut?.length) }),
      source: t('dispatch.preCheck.soldOutSource'),
      rows: (
        <>
          {soldOut?.map((p, index) => (
            <div
              key={`${p.name}-${index}`}
              data-testid={`dispatch-precheck-soldout-${index}`}
              className={ROW}
            >
              <span className="text-sm">{p.name}</span>
              <span className={WHY}>
                {t('dispatch.salesBridge.productCounts', {
                  units: p.units,
                  orders: p.orders,
                })}
              </span>
              <span />
            </div>
          ))}
        </>
      ),
    });
  }

  // A group opens only when it has something to act on or its read failed;
  // loading (count undefined, no error) stays a chip with a dash.
  const isExpanded = (item: CheckItem) => item.error || (item.count ?? 0) > 0;
  const expanded = items.filter(isExpanded);
  const collapsed = items.filter((item) => !isExpanded(item));

  return (
    <section data-testid="dispatch-pre-checks" data-date={date} className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <h3 className="text-[14.5px] font-semibold">{t('dispatch.preCheck.title')}</h3>
        {expanded.length > 0 ? (
          <span className="text-xs text-muted-foreground">{t('dispatch.preCheck.subtitle')}</span>
        ) : null}
        {collapsed.map((item) => (
          <span
            key={item.key}
            data-testid={item.testId}
            className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"
          >
            {item.label}
          </span>
        ))}
      </div>
      {expanded.length > 0 ? (
        <div
          data-testid="dispatch-precheck-expanded"
          className="overflow-hidden rounded-xl bg-surface-1 outline outline-1 -outline-offset-1 outline-border"
        >
          {expanded.map((item) => (
            <Group
              key={item.key}
              testId={item.testId}
              badge={<Badge className={item.tone}>{item.label}</Badge>}
              source={item.source}
              error={item.error ? loadError : null}
            >
              {item.rows}
            </Group>
          ))}
        </div>
      ) : null}
    </section>
  );
}
